import { useState, type ReactNode } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, Vacio, BotonAccion, Modal, formatFecha, confirmar } from '../shared/ui.tsx';
import { Instrucciones } from './MiAgenda.tsx';
import { formatoRango } from '../shared/horas.ts';

interface FilaConvocatoria {
  id: string;
  solicitud_id: string;
  estado: string;
  enviada_en: string;
  vence_en: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  salon_nombre: string;
  cliente_nombre: string;
  direccion: string | null;
  categoria_nombre: string;
  notas: string | null;
  instrucciones: string | null;
  instrucciones_turno: string | null;
}

type FiltroTiempo = 'todas' | 'urgentes' | 'semana' | 'mes';

const ETIQUETAS_TIEMPO: Record<FiltroTiempo, string> = {
  todas: 'Todos los horarios',
  urgentes: '⚡ Urgentes 48h',
  semana: 'Esta semana',
  mes: 'Este mes',
};

function esUrgente(c: FilaConvocatoria): boolean {
  const inicio = new Date(`${c.fecha}T${c.hora_inicio}:00`);
  const diff = inicio.getTime() - Date.now();
  return diff > -12 * 3600_000 && diff <= 48 * 3600_000;
}

function esEstaSemana(c: FilaConvocatoria): boolean {
  const hoy = new Date();
  const dow = hoy.getDay() === 0 ? 7 : hoy.getDay();
  const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (dow - 1));
  const domingo = new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + 6);
  const f = new Date(`${c.fecha}T00:00:00`);
  return f >= lunes && f <= domingo;
}

function esEsteMes(c: FilaConvocatoria): boolean {
  const hoy = new Date();
  const f = new Date(`${c.fecha}T00:00:00`);
  return f.getFullYear() === hoy.getFullYear() && f.getMonth() === hoy.getMonth();
}

export function ConvocatoriasPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<FilaConvocatoria[]>('/api/convocatorias');
  const [filtroTiempo, setFiltroTiempo] = useState<FiltroTiempo>('todas');
  const [filtroLocal, setFiltroLocal] = useState('');
  const [modalFiltros, setModalFiltros] = useState(false);

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;

  // Las pasadas viven en Historial.
  const hoyStr = new Date().toISOString().slice(0, 10);
  const vigentes = (datos ?? []).filter((c) => c.fecha >= hoyStr);
  const locales = [...new Set(vigentes.map((c) => c.cliente_nombre))].sort();
  const urgentesPendientes = vigentes.filter((c) => c.estado === 'enviada' && esUrgente(c)).length;

  const filtradas = vigentes.filter((c) => {
    if (filtroLocal && c.cliente_nombre !== filtroLocal) return false;
    if (filtroTiempo === 'urgentes') return esUrgente(c) && c.estado === 'enviada';
    if (filtroTiempo === 'semana') return esEstaSemana(c);
    if (filtroTiempo === 'mes') return esEsteMes(c);
    return true;
  });
  const pendientes = filtradas.filter((c) => c.estado === 'enviada');
  const otras = filtradas.filter((c) => c.estado !== 'enviada');
  const hayFiltros = filtroTiempo !== 'todas' || filtroLocal !== '';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-neutro-800">Convocatorias</h2>
        <BotonAccion variante="secundario" onClick={() => setModalFiltros(true)}>
          Filtros{hayFiltros ? ' •' : ''}
        </BotonAccion>
      </div>

      {hayFiltros && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs font-semibold text-neutro-400">Filtrando:</span>
          {filtroTiempo !== 'todas' && (
            <button
              className={`rounded-full px-3 py-1 font-medium text-white ${filtroTiempo === 'urgentes' ? 'bg-amber-600' : 'bg-primario-400'}`}
              onClick={() => setFiltroTiempo('todas')}
            >
              {ETIQUETAS_TIEMPO[filtroTiempo]} ✕
            </button>
          )}
          {filtroLocal && (
            <button className="rounded-full bg-primario-400 px-3 py-1 font-medium text-white" onClick={() => setFiltroLocal('')}>
              {filtroLocal} ✕
            </button>
          )}
        </div>
      )}

      {filtradas.length === 0 && (
        <Vacio texto={vigentes.length === 0 ? 'No tenés convocatorias por ahora.' : 'Nada con este filtro.'} />
      )}

      {pendientes.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold uppercase text-estado-pendiente">Pendientes ({pendientes.length})</h3>
          {pendientes.map((c) => (
            <CardConvocatoria key={c.id} conv={c} onCambio={recargar} />
          ))}
        </section>
      )}

      {otras.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold uppercase text-neutro-400">Próximos servicios</h3>
          {otras.map((c) => (
            <CardConvocatoria key={c.id} conv={c} onCambio={recargar} readOnly />
          ))}
        </section>
      )}

      {modalFiltros && (
        <Modal abierto={true} titulo="Filtros" onCerrar={() => setModalFiltros(false)}>
          <div className="space-y-4 text-sm">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutro-400">Horario</p>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(ETIQUETAS_TIEMPO) as FiltroTiempo[]).map((t) => (
                  <button
                    key={t}
                    className={`rounded-full px-3.5 py-1.5 font-medium transition-colors ${
                      filtroTiempo === t
                        ? (t === 'urgentes' ? 'bg-amber-600 text-white' : 'bg-primario-400 text-white')
                        : 'border border-contorno-var bg-white text-neutro-800'
                    }`}
                    onClick={() => setFiltroTiempo(t)}
                  >
                    {t === 'urgentes' && urgentesPendientes > 0 ? `${ETIQUETAS_TIEMPO[t]} (${urgentesPendientes})` : ETIQUETAS_TIEMPO[t]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutro-400">Local</p>
              <div className="flex flex-wrap gap-2">
                <button
                  className={`rounded-full px-3.5 py-1.5 font-medium ${filtroLocal === '' ? 'bg-primario-400 text-white' : 'border border-contorno-var bg-white text-neutro-800'}`}
                  onClick={() => setFiltroLocal('')}
                >
                  Todos los locales
                </button>
                {locales.map((l) => (
                  <button
                    key={l}
                    className={`rounded-full px-3.5 py-1.5 font-medium ${filtroLocal === l ? 'bg-primario-400 text-white' : 'border border-contorno-var bg-white text-neutro-800'}`}
                    onClick={() => setFiltroLocal(l)}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <BotonAccion variante="secundario" onClick={() => { setFiltroTiempo('todas'); setFiltroLocal(''); }}>Limpiar</BotonAccion>
              <BotonAccion onClick={() => setModalFiltros(false)}>Listo</BotonAccion>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CardConvocatoria({ conv, onCambio, readOnly }: { conv: FilaConvocatoria; onCambio?: () => void; readOnly?: boolean }) {
  const [respondiendo, setRespondiendo] = useState<'aceptar' | 'rechazar' | null>(null);
  const [quitando, setQuitando] = useState(false);
  const urgente = conv.estado === 'enviada' && esUrgente(conv);

  async function responder(accion: 'aceptar' | 'rechazar') {
    setRespondiendo(accion);
    try {
      await apiPost(`/api/convocatorias/${conv.id}/responder`, { accion });
      onCambio?.();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    } finally {
      setRespondiendo(null);
    }
  }

  async function quitar() {
    const pregunta = conv.estado === 'aceptada'
      ? '¿Quitar esta convocatoria de la bandeja? El servicio sigue confirmado y lo ves en Mi agenda; esto NO lo cancela.'
      : '¿Quitar esta convocatoria de la bandeja?';
    if (!confirmar(pregunta)) return;
    setQuitando(true);
    try {
      await apiPost(`/api/convocatorias/${conv.id}/ocultar`, {});
      onCambio?.();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    } finally {
      setQuitando(false);
    }
  }

  const color = conv.estado === 'aceptada' ? 'text-estado-ok'
    : (conv.estado === 'rechazada' || conv.estado === 'vencida') ? 'text-estado-error'
    : 'text-estado-pendiente';

  return (
    <div className={`rounded-2xl bg-white p-4 shadow-sm border ${urgente ? 'border-2 border-amber-500' : 'border-contorno-var'}`}>
      {urgente && <p className="mb-2 text-xs font-bold text-amber-600">⚡ URGENTE — es dentro de las próximas 48 horas</p>}
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-neutro-800">{formatFecha(conv.fecha)}</p>
          <p className="text-sm text-neutro-500">{formatoRango(conv.hora_inicio, conv.hora_fin)}</p>
          <p className="text-sm font-medium">{conv.cliente_nombre} · {conv.salon_nombre}</p>
          {conv.direccion && <p className="text-xs text-neutro-400">{conv.direccion}</p>}
          <p className="mt-1 text-sm">Puesto: <span className="font-semibold">{conv.categoria_nombre}</span></p>
          {conv.notas && <p className="text-xs text-neutro-400">{conv.notas}</p>}
          <Instrucciones turno={conv.instrucciones_turno} individual={conv.instrucciones} />
        </div>
        <span className={`text-sm font-medium capitalize ${color}`}>{conv.estado}</span>
      </div>

      {!readOnly && conv.estado === 'enviada' && (
        <div className="mt-3 flex gap-2">
          <BotonAccion
            onClick={() => { if (confirmar('¿Aceptás esta convocatoria?')) responder('aceptar'); }}
            disabled={respondiendo !== null}
            className="flex-1 bg-estado-ok text-white"
          >
            {respondiendo === 'aceptar' ? '…' : '✓ Acepto'}
          </BotonAccion>
          <BotonAccion
            onClick={() => { if (confirmar('¿Rechazar esta convocatoria?')) responder('rechazar'); }}
            disabled={respondiendo !== null}
            className="flex-1 bg-red-50 text-estado-error"
          >
            {respondiendo === 'rechazar' ? '…' : '✕ No puedo'}
          </BotonAccion>
        </div>
      )}

      {conv.estado !== 'enviada' && (
        <button
          className="mt-3 text-xs font-semibold text-neutro-400 underline hover:no-underline disabled:opacity-50"
          onClick={() => void quitar()}
          disabled={quitando}
        >
          {quitando ? 'Quitando…' : '✕ Quitar de la bandeja'}
        </button>
      )}
    </div>
  );
}
